/**
 * Marketplace Page - Public cat listings
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X, SlidersHorizontal } from 'lucide-react';
import { Button, Select, SearchInput, Card, Badge } from '../components/shared/UIComponents';
import { CatGrid } from '../components/marketplace/CatCard';
import { getAvailableCats, SA_CAT_BREEDS, SA_PROVINCES } from '../firebase/firestore';
import { formatCurrency, cn } from '../utils/helpers';

export const MarketplacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filters from URL params
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    breed: searchParams.get('breed') || '',
    sex: searchParams.get('sex') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    province: searchParams.get('province') || ''
  });

  useEffect(() => {
    loadCats();
  }, []);

  const loadCats = async () => {
    try {
      setLoading(true);
      const data = await getAvailableCats(filters);
      setCats(data);
    } catch (error) {
      console.error('Error loading cats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, val]) => {
      if (val) params.set(key, val);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      breed: '',
      sex: '',
      minPrice: '',
      maxPrice: '',
      province: ''
    });
    setSearchParams({});
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  // Filter cats client-side for now
  const filteredCats = cats.filter(cat => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const searchable = `${cat.name} ${cat.breed} ${cat.color}`.toLowerCase();
      if (!searchable.includes(search)) return false;
    }
    if (filters.breed && cat.breed !== filters.breed) return false;
    if (filters.sex && cat.sex !== filters.sex) return false;
    if (filters.minPrice && cat.price < Number(filters.minPrice)) return false;
    if (filters.maxPrice && cat.price > Number(filters.maxPrice)) return false;
    return true;
  });

  const breedOptions = SA_CAT_BREEDS.map(b => ({ value: b.code, label: b.name }));
  const provinceOptions = SA_PROVINCES.map(p => ({ value: p, label: p }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-display font-bold text-gray-900 mb-2">
            Cat Marketplace
          </h1>
          <p className="text-gray-500">
            Find your perfect pedigreed cat from verified South African breeders
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <FilterPanel
              filters={filters}
              onChange={handleFilterChange}
              onClear={clearFilters}
              breedOptions={breedOptions}
              provinceOptions={provinceOptions}
            />
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Mobile Search & Filter Bar */}
            <div className="lg:hidden mb-6 space-y-4">
              <SearchInput
                placeholder="Search cats..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowFilters(true)}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="primary" className="ml-2">{activeFilterCount}</Badge>
                )}
              </Button>
            </div>

            {/* Desktop Search */}
            <div className="hidden lg:block mb-6">
              <SearchInput
                placeholder="Search by name, breed, or color..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="max-w-md"
              />
            </div>

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-500">
                {filteredCats.length} cat{filteredCats.length !== 1 ? 's' : ''} found
              </p>
              
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-feline-600 hover:text-feline-700 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>

            {/* Active Filters Tags */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {filters.breed && (
                  <FilterTag
                    label={`Breed: ${SA_CAT_BREEDS.find(b => b.code === filters.breed)?.name || filters.breed}`}
                    onRemove={() => handleFilterChange('breed', '')}
                  />
                )}
                {filters.sex && (
                  <FilterTag
                    label={`Sex: ${filters.sex}`}
                    onRemove={() => handleFilterChange('sex', '')}
                  />
                )}
                {filters.minPrice && (
                  <FilterTag
                    label={`Min: ${formatCurrency(filters.minPrice)}`}
                    onRemove={() => handleFilterChange('minPrice', '')}
                  />
                )}
                {filters.maxPrice && (
                  <FilterTag
                    label={`Max: ${formatCurrency(filters.maxPrice)}`}
                    onRemove={() => handleFilterChange('maxPrice', '')}
                  />
                )}
              </div>
            )}

            {/* Cat Grid */}
            <CatGrid 
              cats={filteredCats} 
              loading={loading}
              emptyMessage="No cats match your filters. Try adjusting your search criteria."
            />
          </main>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Filters</h2>
              <button onClick={() => setShowFilters(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <FilterPanel
                filters={filters}
                onChange={handleFilterChange}
                onClear={clearFilters}
                breedOptions={breedOptions}
                provinceOptions={provinceOptions}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Filter Panel Component
const FilterPanel = ({ filters, onChange, onClear, breedOptions, provinceOptions }) => (
  <Card className="sticky top-24">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-semibold text-gray-900">Filters</h2>
      <button
        onClick={onClear}
        className="text-sm text-feline-600 hover:text-feline-700"
      >
        Clear all
      </button>
    </div>

    <div className="space-y-4">
      <Select
        label="Breed"
        value={filters.breed}
        onChange={(e) => onChange('breed', e.target.value)}
        options={breedOptions}
        placeholder="All breeds"
      />

      <Select
        label="Sex"
        value={filters.sex}
        onChange={(e) => onChange('sex', e.target.value)}
        options={[
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' }
        ]}
        placeholder="Any"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Price Range (ZAR)
        </label>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.minPrice}
            onChange={(e) => onChange('minPrice', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.maxPrice}
            onChange={(e) => onChange('maxPrice', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none"
          />
        </div>
      </div>

      <Select
        label="Province"
        value={filters.province}
        onChange={(e) => onChange('province', e.target.value)}
        options={provinceOptions}
        placeholder="All provinces"
      />
    </div>
  </Card>
);

// Filter Tag Component
const FilterTag = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-3 py-1 bg-feline-100 text-feline-700 rounded-full text-sm">
    {label}
    <button onClick={onRemove} className="hover:text-feline-900">
      <X className="w-3 h-3" />
    </button>
  </span>
);

export default MarketplacePage;
